import React, { useEffect, useRef, useState, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import AnimatedMainContainer from "../../../components/AnimatedMainContainer";
import { useToast } from "../../../hooks/toast";
import * as Yup from "yup";
import getValidationErrors from "../../../utils/getValidationErrors";
import api from "../../../service";
import { useCallback } from "react";
import InputWithLabel from "../../../components/InputWithLabel";
import MaskedInputWithLabel from "../../../components/MaskedInputWithLabel";
import FormButton from "../../../components/FormButton";
import { Form } from "@unform/web";
import { validateSingleFieldOnBlur } from "../../../utils/formValidations";

import {
  Container,
  FormContainer,
  TitleContainer,
  Title,
  FieldsContainer,
  Horizontal,
  FilterActionsContainer,
  SubmitContainer,
  LinkButton,
  ActionContainer,
  BadgeContainer,
  NotificationContainer,
} from "./styles";
import Table from "../../../components/Table";
import Badge from "../../../components/Badge";
import ConfirmationModal from "../../../components/ConfirmationModal";
import { useLoading } from "../../../hooks/loading";
import SelectWithLabel from "../../../components/SelectWithLabel";
import useGenericDocumentMask from "../../../utils/useGenericDocumentMask";

const AdminAssociateCollaborators = () => {
  const { token } = useSelector((state) => state.auth);
  const [data, setData] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [collaboratorId, setCollaboratorId] = useState(null);
  const formRef = useRef(null);
  const intl = useIntl();
  const history = useHistory();
  const { addToast } = useToast();
  const { handleLoading } = useLoading();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState(false);
  const [documentMask, setDocumentMask] = useGenericDocumentMask();
  const [selectedRoleType, setSelectedRoleType] = useState();
  const [haveLoadedUsers, setHaveLoadedUsers] = useState(false);

  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [submittedForm, setSubmittedForm] = useState({
    document: "",
    name: "",
    userId: 0,
    roleType: "",
  });

  const roleTypes = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: "exhibitor" }),
        value: "EXHIBITOR",
      },
      {
        label: intl.formatMessage({ id: "sponsor_without_m2" }),
        value: "SPONSOR_WITHOUT_M2",
      },
      {
        label: intl.formatMessage({ id: "service_provider" }),
        value: "SERVICE_PROVIDER",
      },
    ],
    [intl]
  );

  const isAllRequiredFieldsEmpty = () => {
    const name = formRef.current.getFieldValue("name");
    const document = formRef.current.getFieldValue("document");
    const userCompany = formRef.current.getFieldValue("user");

    return name || document || userCompany;
  };

  useEffect(()=>{
    const params = new URLSearchParams(document.location.search);
    if (!roleTypes) return;
    if (params.get("user") && !users) return;
    if (!params.get("name") && !params.get("user") && !params.get("document")) return;
    if (params.get("name"))
      formRef.current.setFieldValue("name", params.get("name"));
    if (params.get("user"))
      formRef.current.setFieldValue("user", {
        label: users.filter(
          (i) => i.value === Number(params.get("user"))
        )[0]?.label,
        value: params.get("user")
      });
    if (params.get("document"))
      formRef.current.getFieldRef("document").setInputValue(params.get("document"));
    if (params.get("roleType"))
      formRef.current.setFieldValue("roleType", {
        label: roleTypes.filter(
          (i) => i.value === params.get("roleType")
        )[0]?.label,
        value: params.get("roleType")
      });

    const formData = {
      name: params.get("name"),
      document: params.get("document"),
      userId: params.get("user"),
      roleType: params.get("roleType"),
    };

    setSubmittedForm(formData);
    fetchData(0, perPage, formData, true);

    if (haveLoadedUsers) {
      history.push("/admin/associate-collaborators");
    }
  }, [haveLoadedUsers]);

  const schema = Yup.object().shape({
    document: Yup.string()
      .nullable()
      .test(
        "is-all-required-fields-empty-document",
        intl.formatMessage({
          id: "error.name_document_company_required",
        }),
        isAllRequiredFieldsEmpty
      ),
    name: Yup.string()
      .nullable()
      .test(
        "is-all-required-fields-empty-name",
        intl.formatMessage({
          id: "error.name_document_company_required",
        }),
        isAllRequiredFieldsEmpty
      ),
    user: Yup.string()
      .nullable()
      .test(
        "is-all-required-fields-empty-user",
        intl.formatMessage({
          id: "error.name_document_company_required",
        }),
        isAllRequiredFieldsEmpty
      ),
    roleType: Yup.string(),
  });

  const handleDocumentChange = useCallback(
    (e) => {
      const upperCaseValue = e.target.value.toUpperCase();
      formRef.current.setFieldValue("document", upperCaseValue);
      e.target.value = upperCaseValue;
      setDocumentMask(e);
    },
    [setDocumentMask],
  );

  const fetchData = useCallback(
    async (
      page,
      size = perPage,
      requestBody = submittedForm,
      resetCurrentPage
    ) => {
      setLoading(true);
      if (loading) return;

      if (resetCurrentPage) setCurrentPage(1);

      api
        .patch(
          `/associate-collaborators/admin?page=${page}&per_page=${size}`,
          requestBody,
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "*/*",
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((result) => {
          setData(result.data.associateCollaboratorListOutput);
          setTotalRows(result.data.paginationInfo.totalElements);
          setFiltered(true);
        })
        .catch((err) =>
          addToast({
            type: "error",
            title: intl.formatMessage({
              id: "error",
            }),
            description: intl.formatMessage({
              id: "api.error." + err.response.data.code,
            }),
          })
        )
        .finally(() => setLoading(false));
    },
    [addToast, intl, loading, perPage, submittedForm, token]
  );

  const showConfirmation = () => {
    setModalIsOpen(true);
  };

  useEffect(() => {
    formRef.current.setFieldValue("user");
    setUsers([]);
    if (selectedRoleType) {
      handleLoading(true);
      api
        .get(`/users/active-clients-by-type`, {
          params: {
            role_type: selectedRoleType,
          },
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
          },
        })
        .then((result) => {
          const users = result.data.users.map((user) => ({
            label: user.commercialName,
            value: user.id,
          }));
          setUsers(users);
          setHaveLoadedUsers(true);
        })
        .catch((err) =>
          addToast({
            type: "error",
            title: intl.formatMessage({
              id: "error",
            }),
            description: intl.formatMessage({
              id: "api.error." + err.response.data.code,
            }),
          })
        )
        .finally(() => handleLoading(false));
    }
  }, [selectedRoleType, addToast, intl, token, handleLoading]);

  const handleSubmit = useCallback(
    async (data) => {
      try {
        formRef.current?.setErrors({});

        await schema.validate(data, { abortEarly: false });

        setLoading(true);
        if (loading) {
          return;
        }

        const formData = {
          name: data.name,
          document: data.document,
          userId: data.user,
          roleType: data.roleType,
        };

        setSubmittedForm(formData);
        fetchData(0, perPage, formData, true);
      } catch (err) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);

          return;
        }
      }
    },
    [fetchData, loading, perPage, schema]
  );

  const handleDeletion = () => {
    handleLoading(true);
    api
      .delete(`/associate-collaborators/admin/${collaboratorId}`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((_) => {
        addToast({
          type: "success",
          title: intl.formatMessage({
            id: "success",
          }),
          description: intl.formatMessage({
            id: "success.collaborator_deletion",
          }),
        });
        setData(data.filter((value) => value.id !== collaboratorId));
        setCollaboratorId(null);
        setModalIsOpen(false);
      })
      .catch((err) => {
        setCollaboratorId(null);
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: "api.error." + err.response.data.code,
          }),
        });
      })
      .finally(() => {
        handleLoading(false);
      });
  };

  const handlePageChange = (page) => {
    fetchData(page - 1);
    setCurrentPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    fetchData(page - 1, newPerPage);
    setPerPage(newPerPage);
  };

  const validationBlur = (name) => {
    let errors = formRef.current.getErrors();

    validateSingleFieldOnBlur(errors, schema, name, formRef);
  };

  const clearFields = () => {
    history.push("/admin/associate-collaborators");
    formRef.current.reset();
    formRef.current.setFieldValue("user");
    setUsers([]);
    formRef.current.setFieldValue("name");
    formRef.current.getFieldRef("document").setInputValue("");
    formRef.current.setFieldValue("roleType");
    setSubmittedForm({
      document: "",
      name: "",
      userId: 0,
      roleType: "",
    });
    setFiltered(false);
  };

  const columns = [
    {
      name: intl.formatMessage({ id: "name" }),
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: intl.formatMessage({ id: "document_filter" }),
      selector: (row) => row.document,
      minWidth: "150px",
      maxWidth: "150px",
    },
    {
      name: intl.formatMessage({ id: "assignment" }),
      selector: (row) => row.jobTitle,
      sortable: true,
      maxWidth: "200px",
    },
    {
      name: intl.formatMessage({ id: "email" }),
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: intl.formatMessage({ id: "company" }),
      selector: (row) => row.company,
      sortable: true,
    },
    {
      cell: (row) => (
        <>
          <LinkButton onClick={()=> handleEditClick(row.id)}>
            <Badge color="#002244" text={intl.formatMessage({ id: "edit" })} />
          </LinkButton>

          <Badge
            onClick={() => {
              setCollaboratorId(row.id);
              showConfirmation();
            }}
            color="#cc0000"
            text={intl.formatMessage({ id: "delete" })}
          />
        </>
      ),
      right: true,
    },
  ];

  const buildReturnUrl = () => {
    const initialURL = `/admin/associate-collaborators?`;
    const partialURLName = !submittedForm.name
      ? ""
      : `&name=${submittedForm.name}`;
    const partialURLUser = !submittedForm.userId
      ? ""
      : `&user=${submittedForm.userId}`;
    const partialURLDocument = !submittedForm.document
      ? ""
      : `&document=${submittedForm.document}`;
    const partialURLRoleType = !submittedForm.roleType
      ? ""
      : `&roleType=${submittedForm.roleType}`;

    return (
      initialURL + partialURLName + partialURLUser + partialURLDocument + partialURLRoleType
    );
  };

  const handleEditClick = (id) => {
    history.push(buildReturnUrl());
    history.push(`/admin/associate-collaborator/edit/${id}`);
  }

  return (
    <AnimatedMainContainer
      breadcrumb={intl.formatMessage({
        id: "breadcrumb.consult_registered_collaborators",
      })}
    >
      <Container>
        <FormContainer>
          <Form
            ref={formRef}
            onSubmit={handleSubmit}
          >
            <TitleContainer>
              <Title>
                <FormattedMessage id="search_for_collaborators" />
              </Title>
              <ActionContainer>
                <BadgeContainer onClick={() => history.push("/dashboard")}>
                  <Badge
                    color="#C9C9C9"
                    text={intl.formatMessage({ id: "goback" })}
                    fontSize="12px"
                  />
                </BadgeContainer>
              </ActionContainer>
            </TitleContainer>
            <FieldsContainer>
              <Horizontal>
                <InputWithLabel
                  name="name"
                  validationBlur={validationBlur}
                  label={intl.formatMessage({ id: "name" })}
                />
                <MaskedInputWithLabel
                  name="document"
                  mask={documentMask}
                  maskChar={null}
                  onChange={handleDocumentChange}
                  validationBlur={validationBlur}
                  label={intl.formatMessage({ id: "document_cpf_or_passport" })}
                />
              </Horizontal>
              <Horizontal>
                <SelectWithLabel
                  name="roleType"
                  label={intl.formatMessage({ id: "type" })}
                  placeholder={intl.formatMessage({ id: "type" })}
                  validationBlur={validationBlur}
                  options={roleTypes}
                  onChange={(type) => setSelectedRoleType(type?.value)}
                  clearable
                />
                <SelectWithLabel
                  name="user"
                  validationBlur={validationBlur}
                  label={intl.formatMessage({ id: "company" })}
                  placeholder={intl.formatMessage({ id: "company" })}
                  options={users}
                  clearable
                />
              </Horizontal>
              <Horizontal>
                <FilterActionsContainer>
                  <SubmitContainer>
                    <FormButton
                      color="#C9C9C9"
                      type="button"
                      onClick={clearFields}
                    >
                      <FormattedMessage id="clear_fields" />
                    </FormButton>
                  </SubmitContainer>
                  <SubmitContainer>
                    <FormButton type="submit" loading={loading}>
                      <FormattedMessage id="search" />
                    </FormButton>
                  </SubmitContainer>
                </FilterActionsContainer>
              </Horizontal>
            </FieldsContainer>
          </Form>
        </FormContainer>
      </Container>
      {filtered ? (
        <>
          <Table
            columns={columns}
            data={data}
            title={intl.formatMessage({ id: "results" })}
            progressPending={loading}
            paginationServer
            paginationTotalRows={totalRows}
            paginationDefaultPage={currentPage}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
          />
        </>
      ) : (
        <NotificationContainer>
          <FormattedMessage id="need_to_filter_first" />
        </NotificationContainer>
      )}
      <ConfirmationModal
        setIsOpen={setModalIsOpen}
        modalIsOpen={modalIsOpen}
        confirmationFunction={handleDeletion}
        description={intl.formatMessage({ id: "exclusion_confirmation" })}
        confirmationText={intl.formatMessage({ id: "yes" })}
        cancelText={intl.formatMessage({ id: "no" })}
      />
    </AnimatedMainContainer>
  );
};

export default AdminAssociateCollaborators;
